import React, {useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import {useWardrobe} from "../context/WardrobeContext";

/**
 * Экспорт данных пользователя (право на переносимость, 152-ФЗ).
 *
 * Две выгрузки: гардероб (вещи и списки) и полный экспорт — всё, что хранит сервис
 * (CTHS-228). Обе кнопки secondary: единственный primary на регионе решения забрал блок
 * архива с фотографиями (`FullExportZipBlock`) — это и есть полная выгрузка, ради которой
 * сюда приходят; JSON-выгрузки остались быстрым путём для тех, кому фото не нужны.
 *
 * Экспорт гардероба отдаёт только собственные данные: чужой `ownerId` бэкенд отвергает
 * с 403 (CTHS-60), поэтому параметр шлём лишь для собственного гардероба.
 */

/** Сохраняет JSON-ответ файлом через Blob (сервис отдаёт готовый объект). */
const downloadJson = (data: unknown, filename: string): void => {
    const blob = new Blob([JSON.stringify(data, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

type Kind = 'wardrobe' | 'full';

const ExportWardrobeButton: React.FC = () => {
    const {selectedOwnerId} = useWardrobe();
    const [busy, setBusy] = useState<Kind | null>(null);
    const [msg, setMsg] = useState<string | null>(null);

    const exportWardrobe = async () => {
        setBusy('wardrobe');
        setMsg(null);
        try {
            const api = await createAxiosInstance();
            const params = selectedOwnerId ? {ownerId: selectedOwnerId} : {};
            const res = await api.get('/export/wardrobe', {params});
            downloadJson(res.data, 'wardrobe-export.json');
            setMsg('Экспорт скачан');
        } catch (e) {
            setMsg('Не удалось выполнить экспорт');
        } finally {
            setBusy(null);
        }
    };

    const exportFull = async () => {
        setBusy('full');
        setMsg(null);
        try {
            const api = await createAxiosInstance();
            // ownerId не передаём: полный экспорт всегда только для владельца токена.
            const res = await api.get('/export/full');
            downloadJson(res.data, 'clothes-data-export.json');
            setMsg('Файл со всеми данными скачан');
        } catch (e) {
            setMsg('Не удалось выгрузить данные');
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-3 flex-wrap">
                <button onClick={exportWardrobe} disabled={busy !== null} data-testid="export-btn"
                        className="btn-secondary min-h-[44px] disabled:opacity-50">
                    {busy === 'wardrobe' ? 'Готовим…' : 'Экспортировать гардероб'}
                </button>
                <button onClick={exportFull} disabled={busy !== null} data-testid="export-full-btn"
                        className="btn-secondary min-h-[44px] disabled:opacity-50">
                    {busy === 'full' ? 'Собираем…' : 'Скачать все мои данные'}
                </button>
                {msg && (
                    <span className="text-sm text-gray-600 dark:text-gray-300" data-testid="export-msg">{msg}</span>
                )}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
                Полный экспорт включает профиль, вещи, списки, образы, историю носки, согласия и
                настройки. Пароли и ключи внешних сервисов в файл не попадают.
            </p>
        </div>
    );
};

export default ExportWardrobeButton;
